import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Bed, Bath, Square, MapPin, Calendar, ChevronLeft, ChevronRight, 
  Heart, Share2, Phone, Mail, ArrowLeft, Loader2, Home, Car,
  Thermometer, Droplets, TreePine
} from 'lucide-react';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import BookingModal from '@/components/BookingModal';
import { toast } from 'sonner';

const MLSPropertyDetail = () => {
  const { mlsId } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchProperty();
    // Track anonymous property view for gating
    import('@/components/PropertyGate').then(m => m.trackPropertyView && m.trackPropertyView());
  }, [mlsId]);

  const fetchProperty = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/mls/properties/${mlsId}`);
      setProperty(response.data);
    } catch (err) {
      console.error('Error fetching property:', err);
      if (err.response?.status === 404) {
        setError('Property not found. It may have been sold or removed from the market.');
      } else {
        setError('Failed to load property details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const text = property ? `Check out this property: ${property.fullAddress} - ${formatPrice(property.listPrice)}` : '';
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Property Listing', text, url });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  const nextPhoto = () => {
    if (property?.photos?.length) {
      setCurrentPhotoIndex((prev) => (prev + 1) % property.photos.length);
    }
  };

  const prevPhoto = () => {
    if (property?.photos?.length) {
      setCurrentPhotoIndex((prev) => (prev - 1 + property.photos.length) % property.photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#BCA480]" />
        <span className="ml-3 text-gray-600">Loading property details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Property Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/mls')} className="bg-[#BCA480] hover:bg-[#a89470]">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Button>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const photos = property.photos || [];
  const currentPhoto = photos[currentPhotoIndex];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mls-detail-page">
      <SEO 
        title={`${property.fullAddress} | ${formatPrice(property.listPrice)} | Bespoke Realty`}
        description={`${property.bedrooms} bed, ${property.bathrooms} bath home for sale in ${property.address?.city}, TN. ${property.area?.toLocaleString()} sqft. View photos and details.`}
        keywords={`${property.address?.city} real estate, homes for sale ${property.address?.city} TN, ${property.bedrooms} bedroom home`}
      />

      {/* Back Navigation */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/mls')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search Results
          </Button>
        </div>
      </div>

      {/* Photo Gallery */}
      <section className="bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="relative h-[50vh] md:h-[60vh]">
            {currentPhoto ? (
              <img 
                src={currentPhoto} 
                alt={`Property photo ${currentPhotoIndex + 1}`}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <span>No photos available</span>
              </div>
            )}

            {/* Photo Navigation */}
            {photos.length > 1 && (
              <>
                <button 
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button 
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                
                {/* Photo Counter */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                  {currentPhotoIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="flex gap-2 p-4 overflow-x-auto bg-gray-900">
              {photos.slice(0, 10).map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  className={`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 ${
                    idx === currentPhotoIndex ? 'border-[#BCA480]' : 'border-transparent'
                  }`}
                >
                  <img 
                    src={photo} 
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
              {photos.length > 10 && (
                <div className="flex-shrink-0 w-20 h-16 rounded bg-gray-800 flex items-center justify-center text-white text-sm">
                  +{photos.length - 10} more
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Property Info */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 text-sm font-semibold rounded ${
                        property.status === 'Active' ? 'bg-green-100 text-green-800' :
                        property.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {property.status}
                      </span>
                      {property.daysOnMarket > 0 && (
                        <span className="text-gray-500 text-sm">
                          {property.daysOnMarket} days on market
                        </span>
                      )}
                    </div>
                    
                    <h1 className="text-3xl md:text-4xl font-bold text-[#BCA480] mb-2" data-testid="property-price">
                      {formatPrice(property.listPrice)}
                    </h1>
                    
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="h-5 w-5 mr-2 text-[#BCA480]" />
                      <span className="text-lg" data-testid="property-address">{property.fullAddress}</span>
                    </div>

                    <div className="flex flex-wrap gap-6 text-gray-700">
                      <div className="flex items-center">
                        <Bed className="h-5 w-5 mr-2 text-[#BCA480]" />
                        <span className="font-semibold">{property.bedrooms}</span>
                        <span className="ml-1 text-gray-500">Beds</span>
                      </div>
                      <div className="flex items-center">
                        <Bath className="h-5 w-5 mr-2 text-[#BCA480]" />
                        <span className="font-semibold">{property.bathrooms}</span>
                        <span className="ml-1 text-gray-500">Baths</span>
                      </div>
                      {property.area > 0 && (
                        <div className="flex items-center">
                          <Square className="h-5 w-5 mr-2 text-[#BCA480]" />
                          <span className="font-semibold">{property.area.toLocaleString()}</span>
                          <span className="ml-1 text-gray-500">Sq Ft</span>
                        </div>
                      )}
                      {property.garageSpaces > 0 && (
                        <div className="flex items-center">
                          <Car className="h-5 w-5 mr-2 text-[#BCA480]" />
                          <span className="font-semibold">{property.garageSpaces}</span>
                          <span className="ml-1 text-gray-500">Garage</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={handleShare}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Description */}
              {property.remarks && (
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold mb-4">Property Description</h2>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {property.remarks}
                  </p>
                </div>
              )}

              {/* Property Details */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-4">Property Details</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {property.type && (
                    <div>
                      <span className="text-gray-500 text-sm">Property Type</span>
                      <p className="font-semibold">{property.type}</p>
                    </div>
                  )}
                  {property.subType && (
                    <div>
                      <span className="text-gray-500 text-sm">Sub Type</span>
                      <p className="font-semibold">{property.subType}</p>
                    </div>
                  )}
                  {property.yearBuilt && (
                    <div>
                      <span className="text-gray-500 text-sm">Year Built</span>
                      <p className="font-semibold">{property.yearBuilt}</p>
                    </div>
                  )}
                  {property.stories && (
                    <div>
                      <span className="text-gray-500 text-sm">Stories</span>
                      <p className="font-semibold">{property.stories}</p>
                    </div>
                  )}
                  {property.lotSize && (
                    <div>
                      <span className="text-gray-500 text-sm">Lot Size</span>
                      <p className="font-semibold">{property.lotSize}</p>
                    </div>
                  )}
                  {property.style && (
                    <div>
                      <span className="text-gray-500 text-sm">Style</span>
                      <p className="font-semibold">{property.style}</p>
                    </div>
                  )}
                  {property.heating && (
                    <div>
                      <span className="text-gray-500 text-sm">Heating</span>
                      <p className="font-semibold">{property.heating}</p>
                    </div>
                  )}
                  {property.cooling && (
                    <div>
                      <span className="text-gray-500 text-sm">Cooling</span>
                      <p className="font-semibold">{property.cooling}</p>
                    </div>
                  )}
                  {property.pool && (
                    <div>
                      <span className="text-gray-500 text-sm">Pool</span>
                      <p className="font-semibold">{property.pool}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              {(property.interiorFeatures || property.exteriorFeatures) && (
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold mb-4">Features</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {property.interiorFeatures && (
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2">Interior Features</h3>
                        <p className="text-gray-600">{property.interiorFeatures}</p>
                      </div>
                    )}
                    {property.exteriorFeatures && (
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2">Exterior Features</h3>
                        <p className="text-gray-600">{property.exteriorFeatures}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* MLS Info */}
              <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600">
                <p>MLS# {property.mlsId || property.listingId}</p>
                {property.listDate && (
                  <p>Listed: {new Date(property.listDate).toLocaleDateString()}</p>
                )}
                {property.mlsArea && <p>MLS Area: {property.mlsArea}</p>}
              </div>
            </div>

            {/* Sidebar - Contact Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg p-6 shadow-sm sticky top-4">
                <h2 className="text-xl font-bold mb-4">Interested in this property?</h2>
                <p className="text-gray-600 mb-6">
                  Contact Bespoke Realty to schedule a viewing or get more information about this listing.
                </p>
                
                <Button 
                  className="w-full bg-[#BCA480] hover:bg-[#a89470] mb-3"
                  size="lg"
                  onClick={() => setShowBooking(true)}
                  data-testid="schedule-tour-btn"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule a Tour
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full mb-3"
                  size="lg"
                  asChild
                >
                  <a href="tel:+19013904414">
                    <Phone className="h-4 w-4 mr-2" />
                    Call (901) 390-4414
                  </a>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  size="lg"
                  asChild
                >
                  <Link to="/contact">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Message
                  </Link>
                </Button>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold mb-2">Bespoke Realty</h3>
                  <p className="text-sm text-gray-600">
                    Your trusted partner for real estate in Memphis, TN and North Mississippi.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingModal 
        isOpen={showBooking} 
        onClose={() => setShowBooking(false)} 
        bookingType="property_viewing"
        propertyAddress={property?.fullAddress}
      />
    </div>
  );
};

export default MLSPropertyDetail;
